import { Box, Typography } from '@mui/material';
import { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { IAppointment } from '../../../library/models/appointment';
import ResourcesIds from '../../../resources/ResourcesIds';
import {
    HOUR_HEIGHT,
    ONE_LINE_MAX,
    SHORT_CARD_MAX,
    appointmentServiceStyles,
    appointmentStyles,
    appointmentTimeStyles,
    appointmentTitleStyles,
    emptySlotStyles,
    laneStyles,
    timeBlockStyles,
} from './myCalendar.styles';
import {
    LanedAppointment,
    STATUS_LABELS,
    TONES,
    clockAtMinute,
    clockOf,
    freeStepsOn,
    layoutLanes,
    placeOn,
} from './scheduleEntries';
import { formatDate } from '../../../library/dateTimeHelper';
import { DIARY_START_STEP_MINUTES } from '../../../library/freeSlotTimes';
import { localised } from '../../../library/models/localisedText';

/**
 * How much of a card there is room to print.
 *
 * A week column is about 130px wide, which will not hold what a full-width day column
 * holds. The difference is deliberately a choice of *which* facts to drop rather than a
 * font size: shrinking the text to fit would make the whole week unreadable, whereas the
 * service name is genuinely the least useful thing when the question is "what does my week
 * look like". Nothing dropped here is lost -- the card's aria-label carries all of it, and
 * opening the card shows the rest.
 */
export type Density = 'day' | 'week';

interface DayColumnProps {
    /** The ISO date this column draws, which is half of what an empty slot is worth. */
    day: string;
    appointments: IAppointment[];
    /** The shared axis. Every column on a grid must be handed the same one. */
    firstHour: number;
    /** The other end of it, so the clickable layer stops exactly where the grid does. */
    lastHour: number;
    isPast: (appointment: IAppointment) => boolean;
    isUnresolved: (appointment: IAppointment) => boolean;
    onSelect: (appointment: IAppointment) => void;
    /** An empty slot was clicked, as a wall clock the new-entry dialog can open on. */
    onCreateAt: (startsAt: string) => void;
    density: Density;
}

/**
 * One day's worth of cards, positioned down a lane by the clock.
 *
 * The whole point of having this as a component is that a week is this seven times over
 * against one axis -- the day view is the same thing with a single column.
 */
export const DayColumn = ({
    day,
    appointments,
    firstHour,
    lastHour,
    isPast,
    isUnresolved,
    onSelect,
    onCreateAt,
    density,
}: DayColumnProps): JSX.Element => {
    const { t: translate, i18n } = useTranslation();

    // Measured from the part of the entry on *this* day, held inside the axis -- a block of
    // leave spanning days is a card on each of them, as tall as that day's share of it.
    const offsetOf = (appointment: IAppointment): number =>
        ((placeOn(appointment, day, firstHour, lastHour).top - firstHour * 60) / 60) * HOUR_HEIGHT;

    const heightOf = (appointment: IAppointment): number => {
        const { top, bottom } = placeOn(appointment, day, firstHour, lastHour);
        return ((bottom - top) / 60) * HOUR_HEIGHT;
    };

    const spansDays = (appointment: IAppointment): boolean =>
        appointment.startsAt.slice(0, 10) !== appointment.endsAt.slice(0, 10);

    /**
     * When it is, in full. An entry inside one day is its two clock times; one spanning days
     * names both dates as well, since "14:00–10:00" on a Monday column says nothing true.
     */
    const span = (appointment: IAppointment): string =>
        spansDays(appointment)
            ? `${formatDate(appointment.startsAt, i18n)} ${clockOf(appointment.startsAt)} – `
                + `${formatDate(appointment.endsAt, i18n)} ${clockOf(appointment.endsAt)}`
            : `${clockOf(appointment.startsAt)}–${clockOf(appointment.endsAt)}`;

    /**
     * The time a week card has room for: where the entry starts, on the day it starts.
     * A day a block only continues through has no start of its own to print, and its last
     * day says when it ends instead.
     */
    const weekTime = (appointment: IAppointment): string => {
        if (appointment.startsAt.slice(0, 10) === day) {
            return clockOf(appointment.startsAt);
        }

        return appointment.endsAt.slice(0, 10) === day ? `–${clockOf(appointment.endsAt)}` : '';
    };

    /**
     * Everything the card knows, whether or not it had room to print it.
     *
     * A week card shows the start time and drops the service name; a screen reader is told
     * both regardless, so narrowing the column never narrows what is announced.
     */
    const describe = (appointment: IAppointment): string =>
        [
            appointment.patientName,
            span(appointment),
            translate(STATUS_LABELS[appointment.status]),
            appointment.serviceNameRo
                && localised(appointment.serviceNameRo, appointment.serviceNameEn, i18n.language),
            isUnresolved(appointment)
                ? translate(ResourcesIds.MY_CALENDAR_NEEDS_RESOLVING)
                : undefined,
        ]
            .filter(Boolean)
            .join(' · ');

    /**
     * A block of leave has no patient and no status, so all there is to say is when it is.
     * It still needs a name: it is a button now, and a button with no accessible name is
     * one a screen reader cannot describe.
     */
    const describeBlock = (appointment: IAppointment): string =>
        `${translate(ResourcesIds.APPOINTMENT_TIME_BLOCK)} · ${span(appointment)}`;

    /*
     * Anything sharing an hour is drawn beside it rather than on top of it. Two live
     * appointments cannot overlap -- the API refuses that -- but a cancelled booking and
     * the replacement made in the hour it gave back do, and the card underneath was
     * unreadable and unclickable before this.
     */
    /**
     * The half hours nothing is in, drawn first so every card paints over them.
     *
     * Not focusable, and hidden from a screen reader. A week is seven columns of about
     * twenty-two steps, so making these tab stops would put a hundred and fifty of them
     * between the therapist and their own cards -- and unlike the *Add patient* button in
     * the dialog, this is a pointer shortcut over a route that already exists: *Add* opens
     * the same dialog on the day in view, seeded the way it always was.
     *
     * A slot in the past is still clickable. Typing up this morning's walk-in is ordinary
     * work, and the API refuses a patient booking backwards rather than a therapist.
     */
    const slots: JSX.Element[] = freeStepsOn(
        appointments,
        firstHour,
        lastHour,
        DIARY_START_STEP_MINUTES,
        day
    ).map((minute: number) => (
        <Box
            key={minute}
            aria-hidden
            data-testid="my-calendar-empty-slot"
            data-start={`${day}T${clockAtMinute(minute)}`}
            onClick={() => onCreateAt(`${day}T${clockAtMinute(minute)}`)}
            sx={emptySlotStyles(
                ((minute - firstHour * 60) / 60) * HOUR_HEIGHT,
                (DIARY_START_STEP_MINUTES / 60) * HOUR_HEIGHT
            )}
        />
    ));

    return (
        <Box sx={laneStyles}>
            {slots}

            {layoutLanes(appointments).map(({ appointment, lane, lanes }: LanedAppointment) =>
                appointment.timeBlock ? (
                    <Box
                        key={appointment.id}
                        role="button"
                        tabIndex={0}
                        aria-label={describeBlock(appointment)}
                        onClick={() => onSelect(appointment)}
                        sx={timeBlockStyles(
                            offsetOf(appointment),
                            heightOf(appointment),
                            isPast(appointment),
                            { lane, lanes }
                        )}
                    >
                        {heightOf(appointment) > ONE_LINE_MAX && (
                            <Typography sx={appointmentTimeStyles}>
                                {density === 'week'
                                    ? weekTime(appointment)
                                    : span(appointment)}
                            </Typography>
                        )}
                        <Typography sx={appointmentTitleStyles}>
                            {translate(ResourcesIds.APPOINTMENT_TIME_BLOCK)}
                        </Typography>
                    </Box>
                ) : (
                    <Box
                        key={appointment.id}
                        role="button"
                        tabIndex={0}
                        aria-label={describe(appointment)}
                        onClick={() => onSelect(appointment)}
                        sx={appointmentStyles(
                            offsetOf(appointment),
                            heightOf(appointment),
                            TONES[appointment.status],
                            {
                                isPast: isPast(appointment),
                                needsResolving: isUnresolved(appointment),
                            },
                            { lane, lanes }
                        )}
                    >
                        {/* The first thing to go when there is no room -- see
                            ONE_LINE_MAX. What is left is the patient's name, which is
                            what the therapist is scanning for. */}
                        {heightOf(appointment) > ONE_LINE_MAX && (
                            <Typography sx={appointmentTimeStyles}>
                                {density === 'week'
                                    ? weekTime(appointment)
                                    : span(appointment)}
                                {/* Both of these only fit beside the time on a full-width
                                    day column. In a week they move to the third line, where
                                    the service name would have been -- a week column is too
                                    narrow to hold them, and left here they wrapped and
                                    pushed the patient's name off the bottom of the card. */}
                                {density === 'day' && (
                                    <>
                                        {` · ${translate(STATUS_LABELS[appointment.status])}`}
                                        {/* Said in words as well as drawn as a ring: the
                                            ring alone would be colour-only. */}
                                        {isUnresolved(appointment) &&
                                            ` · ${translate(ResourcesIds.MY_CALENDAR_NEEDS_RESOLVING)}`}
                                    </>
                                )}
                            </Typography>
                        )}
                        <Typography sx={appointmentTitleStyles}>
                            {appointment.patientName}
                        </Typography>
                        {/* Dropped rather than clipped on a half-hour card -- see
                            SHORT_CARD_MAX. That leaves an unanswered half-hour session in a
                            week column carrying its ring and its aria-label but not the
                            words, which is the one place the rule bends: printing them
                            anyway sliced the patient's name in half, and a name nobody can
                            read helps nobody. The day view still spells it out in full. */}
                        {heightOf(appointment) > SHORT_CARD_MAX && (
                            <Typography sx={appointmentServiceStyles}>
                                {density === 'day'
                                    ? localised(appointment.serviceNameRo ?? '', appointment.serviceNameEn, i18n.language)
                                    : translate(
                                          isUnresolved(appointment)
                                              ? ResourcesIds.MY_CALENDAR_NEEDS_RESOLVING
                                              : STATUS_LABELS[appointment.status]
                                      )}
                            </Typography>
                        )}
                    </Box>
                )
            )}
        </Box>
    );
};
